import { forwardRef, Fragment, useId } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as LabelPrimitive from '@radix-ui/react-label';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

import { CheckIcon } from '@paid-ui/icons/check';

const CheckboxRoot = CheckboxPrimitive.Root;
const CheckboxIndicator = CheckboxPrimitive.Indicator;

const checkboxVariants = cva(
  'peer my-0.5 h-5 w-5 shrink-0 rounded border border-grey-dark bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-blue data-[state=checked]:bg-blue data-[state=checked]:text-white',
  {
    variants: {
      changed: {
        true: 'border-purple',
      },
      error: {
        true: 'border-pink focus-visible:ring-pink',
      },
    },
  },
);

export interface CheckboxProps
  extends Omit<React.ComponentPropsWithoutRef<typeof CheckboxRoot>, 'value' | 'defaultValue'> {
  value?: boolean;
  defaultValue?: boolean;
  error?: boolean;
  changed?: boolean;
  full?: boolean;
  children?: React.ReactNode;
  onEnter?: () => void;
  onValueChange?: (value: boolean) => void;
}

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(
  (
    {
      id: customId,
      value,
      defaultValue,
      error,
      changed,
      full,
      className,
      hidden,
      children,
      onValueChange,
      onKeyDown,
      onEnter,
      ...restProps
    },
    ref,
  ) => {
    const fallbackId = useId();

    const handleKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (event) => {
      onKeyDown?.(event);
      if (event.key === 'Enter') {
        onEnter?.();
      }
    };

    if (hidden) {
      return null;
    }

    const id = customId ?? fallbackId;

    return (
      <div className="flex gap-x-2">
        <CheckboxRoot
          ref={ref}
          id={id}
          checked={value}
          defaultChecked={defaultValue}
          onCheckedChange={onValueChange}
          aria-labelledby={`${id}-label`}
          onKeyDown={handleKeyDown}
          className={twMerge(
            checkboxVariants({
              error,
              changed,
              className,
            }),
          )}
          {...restProps}
        >
          <CheckboxIndicator className="flex items-center justify-center">
            <CheckIcon className="h-4 w-4 text-current" strokeWidth={3} />
          </CheckboxIndicator>
        </CheckboxRoot>
        {children ? (
          <LabelPrimitive.Root
            htmlFor={id}
            id={`${id}-label`}
            className={twMerge(
              'peer-disabled:cursor-not-allowed peer-disabled:select-none peer-disabled:opacity-50',
              full ? 'w-full' : '',
            )}
          >
            {children}
          </LabelPrimitive.Root>
        ) : null}
      </div>
    );
  },
);

Checkbox.displayName = 'Checkbox';
